import React, { useState, useEffect, useMemo } from 'react';
import { useFamily } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { subscribeToPayments, subscribeToExpenses } from '@coparent/shared/src/services/expenseService';
import { formatCurrency, getCategoryIcon } from '../utils/expenseUtils';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import { SETTLEMENT_METHODS } from '@coparent/shared/src/utils/constants';
import { Link } from 'react-router-dom';
import { HiArrowLeft, HiChevronDown, HiChevronUp, HiCash } from 'react-icons/hi';
import NotificationBell from '../components/notifications/NotificationBell';

const PaymentHistoryPage = () => {
  const { familyId, family } = useFamily();
  const { currentUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expandedPayment, setExpandedPayment] = useState(null);

  useEffect(() => {
    if (!familyId) return;
    const unsub1 = subscribeToPayments(familyId, setPayments);
    const unsub2 = subscribeToExpenses(familyId, setExpenses);
    return () => { unsub1(); unsub2(); };
  }, [familyId]);

  const expenseMap = useMemo(() => {
    const map = {};
    expenses.forEach(e => { map[e.id] = e; });
    return map;
  }, [expenses]);

  const getMethodLabel = (value) => {
    const m = SETTLEMENT_METHODS.find(s => s.value === value);
    return m ? m.label : value;
  };

  const toggleExpand = (id) => {
    setExpandedPayment(prev => prev === id ? null : id);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div className="flex items-center gap-3">
          <Link
            to="/expenses"
            className="w-9 h-9 rounded-xl flex items-center justify-center text-ink-400 hover:text-ink-600 hover:bg-surface-100 transition-colors"
          >
            <HiArrowLeft className="text-lg" />
          </Link>
          <div>
            <h1 className="page-title">Payment History</h1>
            <p className="page-subtitle">Record of all settlements</p>
          </div>
        </div>
        <NotificationBell />
      </div>

      {!payments.length ? (
        <div className="text-center py-16 text-ink-300">
          <HiCash className="text-4xl mx-auto mb-3 text-ink-200" />
          <p className="text-base font-semibold">No payments yet</p>
          <p className="text-sm mt-1">Payments will appear here when expenses are settled.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {payments.map(payment => {
            const paidByProfile = family?.parentProfiles?.[payment.paidBy];
            const paidToProfile = family?.parentProfiles?.[payment.paidTo];
            const isExpanded = expandedPayment === payment.id;
            const relatedExpenses = payment.expenseIds
              .map(id => expenseMap[id])
              .filter(Boolean);
            const isCurrentUserPayer = payment.paidBy === currentUser.uid;

            return (
              <div key={payment.id} className="card-hover p-4">
                {/* Payment summary row */}
                <button
                  onClick={() => toggleExpand(payment.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-white shadow-warm-sm"
                        style={{ backgroundColor: paidByProfile?.color }}
                      >
                        {paidByProfile?.displayName?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-ink-900">
                          {isCurrentUserPayer ? 'You' : paidByProfile?.displayName}
                          {' paid '}
                          {payment.paidTo === currentUser.uid ? 'you' : paidToProfile?.displayName}
                        </p>
                        <p className="text-xs text-ink-400 mt-0.5">
                          {formatDate(payment.createdAt, 'MMM d, yyyy · h:mm a')}
                          {' · '}
                          {getMethodLabel(payment.method)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold text-ink-900">
                        {formatCurrency(payment.totalAmount)}
                      </p>
                      {isExpanded
                        ? <HiChevronUp className="text-ink-400" />
                        : <HiChevronDown className="text-ink-400" />
                      }
                    </div>
                  </div>
                  <p className="text-xs text-ink-400 mt-1.5 ml-[3.25rem]">
                    {payment.expenseIds.length} expense{payment.expenseIds.length !== 1 ? 's' : ''} settled
                  </p>
                </button>

                {/* Expanded: related expenses */}
                {isExpanded && (
                  <div className="mt-4 pt-3 border-t border-surface-100 space-y-2">
                    {relatedExpenses.length === 0 ? (
                      <p className="text-xs text-ink-400 text-center py-3">
                        Expense details no longer available
                      </p>
                    ) : (
                      relatedExpenses.map(expense => {
                        const expPaidBy = family?.parentProfiles?.[expense.paidBy];
                        return (
                          <div
                            key={expense.id}
                            className="flex items-center gap-3 p-3 rounded-xl bg-surface-50"
                          >
                            <span className="text-lg">{getCategoryIcon(expense.category)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-ink-900 truncate">
                                {expense.description}
                              </p>
                              <p className="text-xs text-ink-400">
                                {formatDate(expense.date)} · Paid by{' '}
                                <span style={{ color: expPaidBy?.color }}>{expPaidBy?.displayName}</span>
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-bold text-ink-900">
                                {formatCurrency(expense.amount)}
                              </p>
                              <p className="text-[0.6875rem] text-ink-400">
                                {formatCurrency(expense.owedAmount)} owed
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
